import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { AiRecommendationRecord, FinanceApiService } from './finance-api.service';

@Component({
  standalone: true,
  selector: 'ain-finance-ai-recommendations',
  imports: [AinPageComponent, AinStateComponent, FormsModule, TableModule, ButtonModule, InputTextModule],
  providers: [MessageService],
  template: `
    <ain-page title="AI Recommendations" description="Advisory-only recommendations with explicit approval gate">
      @if (!capabilityEnabled()) {
        <ain-state type="empty" title="AI finance capability is disabled" message="Enable financial-core.ai-finance in control center." />
      } @else {
      <p class="warning">AI recommendations are advisory only and do not perform direct financial mutations.</p>

      <div class="toolbar">
        <input pInputText [(ngModel)]="recommendationType" placeholder="recommendation type" />
        <input pInputText [(ngModel)]="scoreText" placeholder="score" />
        <input pInputText [(ngModel)]="rationale" placeholder="rationale" />
        <button pButton type="button" label="Create" (click)="create()"></button>
      </div>

      <p-table [value]="recommendations()">
        <ng-template pTemplate="header">
          <tr><th>Type</th><th>Score</th><th>Status</th><th>Rationale</th><th>Action</th></tr>
        </ng-template>
        <ng-template pTemplate="body" let-row>
          <tr>
            <td>{{ row.recommendationType }}</td>
            <td>{{ row.score }}</td>
            <td>{{ row.status }}</td>
            <td>{{ row.rationale }}</td>
            <td>
              <button pButton type="button" size="small" [disabled]="row.status !== 'Pending'" label="Approve" (click)="approve(row.id)"></button>
            </td>
          </tr>
        </ng-template>
      </p-table>
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { display: flex; gap: 8px; margin-bottom: 12px; flex-wrap: wrap; }
    .warning { margin: 0 0 12px 0; color: #9a3412; font-weight: 600; }
  `]
})
export class FinanceAiRecommendationsComponent implements OnInit {
  private readonly api = inject(FinanceApiService);
  private readonly messages = inject(MessageService);

  readonly recommendations = signal<AiRecommendationRecord[]>([]);
  readonly capabilityEnabled = signal(false);
  recommendationType = 'cashflow';
  scoreText = '0.70';
  rationale = '';

  ngOnInit(): void {
    this.api.getCapabilitySetting('financial-core.ai-finance').subscribe({
      next: setting => {
        this.capabilityEnabled.set(setting.isEnabled);
        if (setting.isEnabled) this.load();
      }
    });
  }

  load(): void {
    this.api.listAiRecommendations().subscribe({ next: rows => this.recommendations.set(rows) });
  }

  create(): void {
    const score = Number(this.scoreText);
    if (!this.recommendationType.trim() || Number.isNaN(score)) return;
    this.api.createAiRecommendation({
      recommendationType: this.recommendationType.trim(),
      score,
      rationale: this.rationale.trim() || 'Manual admin suggestion',
      recommendationJson: '{"action":"review"}',
      promptJson: '{"source":"admin-ui"}',
      inputJson: '{}',
      outputJson: '{}',
      correlationId: `ai-${Date.now()}`
    }).subscribe({
      next: () => {
        this.rationale = '';
        this.load();
      },
      error: () => this.messages.add({ severity: 'error', summary: 'Create AI recommendation failed' })
    });
  }

  approve(recommendationId: string): void {
    this.api.approveAiRecommendation(recommendationId).subscribe({
      next: () => this.load(),
      error: () => this.messages.add({ severity: 'error', summary: 'Approve AI recommendation failed' })
    });
  }
}
